import React from 'react';
import { Bar } from 'react-chartjs-2';
import { Card, CardBody, CardHeader, Row, Col } from 'reactstrap';
import randomColor from 'randomcolor';
import { formatCurrency } from 'utils/fuctions/formatCurrency';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart } from 'chart.js';
import { ColorAreaIcon } from 'views/Report/Protocol/Synthetic/components/Graphics/components/styled';

const ProductSubtotalChart = ({ opportunityData }) => {
  if (!Chart.plugins.getAll().find((p) => p.id === 'datalabels')) {
    Chart.register(ChartDataLabels);
  }

  const prepareSubtotalChartData = () => {
    if (!opportunityData?.length) return { labels: [], datasets: [] };

    const allProductNames = new Set();
    const productColors = {};

    opportunityData.forEach((opportunity) => {
      opportunity.products.forEach((product) => {
        allProductNames.add(product.productName);
        if (!productColors[product.productName]) {
          productColors[product.productName] = randomColor({
            luminosity: 'light',
            hue: 'random'
          });
        }
      });
    });

    const sortedOpportunities = [...opportunityData].sort(
      (a, b) => a.id - b.id
    );
    const labels = sortedOpportunities.map((opportunity) => opportunity.name);

    const datasets = Array.from(allProductNames).map((productName) => {
      const data = sortedOpportunities.map((opportunity) => {
        const product = opportunity.products.find(
          (p) => p.productName === productName
        );
        return product ? product.subtotal : 0;
      });

      return {
        label: productName,
        data,
        backgroundColor: productColors[productName],
        borderColor: productColors[productName],
        borderWidth: 1
      };
    });

    return { labels, datasets, productColors };
  };

  const chartData = prepareSubtotalChartData();

  const subtotalChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top'
      },
      title: {
        display: true,
        text: 'Valor por Produto'
      }
    },
    scales: {
      x: {
        title: {
          display: true,
          text: 'Fase da Oportunidade'
        }
      },
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: 'Valor (R$)'
        },
        ticks: {
          callback: function (value) {
            return formatCurrency
              ? formatCurrency(value, false)
              : `R$ ${value.toFixed(2)}`;
          }
        }
      }
    }
  };

  const getProductTotals = () => {
    if (!opportunityData?.length) return [];

    const productTotals = {};
    opportunityData.forEach((opportunity) => {
      opportunity.products.forEach((product) => {
        if (!productTotals[product.productName]) {
          productTotals[product.productName] = 0;
        }
        productTotals[product.productName] += product.subtotal;
      });
    });

    return Object.entries(productTotals).map(([productName, total]) => ({
      productName,
      total,
      color: chartData.productColors[productName]
    }));
  };

  const productTotals = getProductTotals();

  return (
    <Card>
      <CardHeader>
        <h6 className="surtitle">Visão Geral</h6>
        <h5 className="h3 mb-0">Valor de Produtos por Fase da Oportunidade</h5>
      </CardHeader>
      <CardBody>
        <Row
          style={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <Col>
            <div className="chart">
              <Bar data={chartData} options={subtotalChartOptions} />
            </div>
          </Col>
        </Row>
        <Row mt="3">
          <div
            style={{
              display: 'flex',
              width: '100%',
              flexDirection: 'column',
              alignItems: 'flex-start',
              marginTop: '10px'
            }}
          >
            {productTotals.map((product, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '5px',
                  justifyContent: 'flex-start'
                }}
              >
                <ColorAreaIcon
                  style={{
                    marginTop: '0'
                  }}
                  color={product.color}
                />
                <span
                  style={{
                    marginRight: '0.3rem'
                  }}
                >
                  {product.productName}:{' '}
                </span>
                <span style={{ fontWeight: 'bold' }}>
                  {formatCurrency(product.total, false)}
                </span>
              </div>
            ))}
          </div>
        </Row>
      </CardBody>
    </Card>
  );
};

export default ProductSubtotalChart;
